import Big from 'big.js'
import { fromUnit } from 'metronome-utils'
import useTranslation from 'next-translate/useTranslation'
import { useContext } from 'react'

import { usePrevious } from '../hooks/usePrevious'
import { useShowUserBalances } from '../hooks/useShowUserBalances'
import useSortSynthetics from '../hooks/useSortSynthetics'

import Button from './Button'
import CryptoCounter from './CryptoCounter'
import FiatCounter from './FiatCounter'
import IconContainer from './icons/IconContainer'
import { MetronomeContext } from './MetronomeContext'
import WalletContext from './WalletContext'

export default function MarketAssetList() {
  const { t } = useTranslation()
  const {
    debtPosition,
    selectedPoolData,
    openForbiddenOperationModal,
    syntheticBalances,
    walletBalancesLoaded,
    openOperationDrawer
  } = useContext(MetronomeContext)
  const showUserBalances = useShowUserBalances()
  const { wallet: connectedWallet } = useContext(WalletContext)

  const userSyntheticTokens =
    selectedPoolData?.synthetics.map(msToken => ({
      ...msToken,
      walletBalances: {
        syntheticBalance: {
          balance: syntheticBalances[msToken.address]?.balance || '0',
          usdBalance: syntheticBalances[msToken.address]?.usdBalance || '0'
        }
      }
    })) ?? []

  const { order, sortFn, SortingArrow } = useSortSynthetics(
    userSyntheticTokens,
    'market'
  )

  const noSyntheticBalance = Big(
    userSyntheticTokens.reduce(
      (accumulator, currentValue) =>
        Big(accumulator).plus(
          currentValue.walletBalances.syntheticBalance.balance
        ),
      0
    )
  ).eq(0)

  const orderedUserSyntheticTokens =
    order === 'desc'
      ? userSyntheticTokens.sort(sortFn).reverse()
      : userSyntheticTokens.sort(sortFn)
  const prevUserSyntheticTokens = usePrevious(orderedUserSyntheticTokens)

  const handleOnClick = function (token) {
    if (
      connectedWallet &&
      walletBalancesLoaded &&
      Big(token.walletBalances?.syntheticBalance?.usdBalance || 0).eq(0)
    ) {
      if (Big(debtPosition?.depositInUsd ?? 0).eq(0)) {
        openForbiddenOperationModal('deposit', connectedWallet)
        return
      }
      openForbiddenOperationModal('generate', connectedWallet, token.address)
      return
    }
    openOperationDrawer('swap', token.address)
  }

  return (
    <div className="lg:bg-block text-white lg:w-full lg:rounded-md lg:p-6 lg:pt-4">
      <div className="hidden p-5 text-sm font-bold capitalize lg:flex">
        <div className="lg:w-1/4">
          <SortingArrow sortingType="asset">
            {t('synths-list.asset')}
          </SortingArrow>
        </div>
        <div className="w-1/4">
          <SortingArrow sortingType="interest-rate">
            {t('synth-balance-fee')}
          </SortingArrow>
        </div>
        <div className="w-1/4">
          {!noSyntheticBalance ? (
            <SortingArrow sortingType="synthetic">
              {t('synths-list.balance')}
            </SortingArrow>
          ) : (
            t('synths-list.balance')
          )}
        </div>
        <div className="w-1/4 text-center">{t('synths-list.actions')}</div>
      </div>
      <div className="lg:bg-blue-secondary space-y-4 lg:w-full lg:space-y-11 lg:rounded-lg lg:p-4">
        {orderedUserSyntheticTokens.map((vsToken, index) => (
          <div
            className="bg-blue-secondary w-full rounded-lg p-4 lg:flex lg:rounded-none lg:bg-transparent lg:p-0"
            key={vsToken.symbol}
          >
            <div className="bg-blue-6 mb-0.5 flex items-center rounded-lg px-3 py-2 lg:mb-0 lg:w-1/4 lg:rounded-none lg:bg-transparent lg:p-0">
              <IconContainer
                className="mr-3 h-6 w-6 lg:h-10 lg:w-10"
                name={vsToken.symbol.toLowerCase()}
              />{' '}
              {vsToken.symbol}
            </div>
            <div className="mb-5 mt-4 w-full space-y-2 lg:m-0 lg:flex lg:w-1/2 lg:space-y-0">
              <div className="flex items-center justify-between text-xs lg:w-1/2 lg:justify-start lg:text-base">
                <div className="text-gray-secondary lg:hidden lg:text-white">
                  {t('synth-balance-fee')}
                </div>
                {vsToken.interestRate}%
              </div>
              <div className="flex justify-between text-xs lg:w-1/2 lg:justify-start lg:pr-12 lg:text-base">
                <div className="text-gray-secondary lg:hidden lg:text-white">
                  {t('synths-list.balance')}
                </div>
                <div className="w-full lg:flex lg:flex-col lg:space-y-2">
                  <CryptoCounter
                    disabled={!showUserBalances}
                    end={fromUnit(
                      vsToken?.walletBalances?.syntheticBalance?.balance || 0,
                      vsToken.decimals
                    )}
                    loading={!walletBalancesLoaded}
                    start={Big(
                      fromUnit(
                        prevUserSyntheticTokens?.[index]?.balance || 0,
                        vsToken.decimals
                      )
                    ).toNumber()}
                  />
                  <span className="px-1 lg:hidden">·</span>
                  <span className="lg:text-gray-5">
                    <FiatCounter
                      disabled={!showUserBalances}
                      end={Big(
                        vsToken?.walletBalances?.syntheticBalance?.usdBalance ||
                          '0'
                      ).toNumber()}
                      loading={!walletBalancesLoaded}
                      start={Big(
                        prevUserSyntheticTokens?.[index]?.walletBalances
                          ?.syntheticBalance?.usdBalance || '0'
                      ).toNumber()}
                    />
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center lg:w-1/4">
              <Button
                className="w-full text-sm"
                data-sentry={`operation-button-${vsToken.symbol}`}
                disabled={!connectedWallet || !walletBalancesLoaded}
                onClick={() => handleOnClick(vsToken)}
                size="lg"
              >
                {t('actions.swap')}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
